import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Tooltip from '@mui/material/Tooltip'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import usePageParams from '@shared/hooks/src/usePageParams'

import { PrescriptionDetailsModal } from '@pages/Prescriptions/Prescription'
import LinearProgress from '@components/LinearProgress'
import PaginationComp from '@components/Pagination'
import StatusChip from '@components/StatusChip'
import TableEmpty from '@components/TableEmpty'
import TableLoader from '@components/TableLoader'

import { isCancelled } from '../Prescription/Prescription.utils'
import { RxSortOrder, usePatientPrescriptions } from './PatientPrescriptions.hooks'

export function Inline({ patientId, encounterId, variant, perPage, disableRowsPerPage }) {
  const [selectedRxId, setSelectedRxId] = useState(null)

  const { data, isRefreshing, isPending, order, setOrder } = usePrescriptionsData({ patientId, encounterId, perPage })

  return (
    <Stack spacing={1}>
      <Paper variant={variant} sx={{ overflow: 'hidden', position: 'relative' }}>
        <LinearProgress loading={isRefreshing} />
        <Content data={data} isLoading={isPending} order={order} onOrderChange={setOrder} onSelect={setSelectedRxId} perPage={perPage} />
      </Paper>
      <Pagination data={data} isLoading={isPending} perPage={perPage} disableRowsPerPage={disableRowsPerPage} />
      <PrescriptionDetailsModal prescriptionId={selectedRxId} open={Boolean(selectedRxId)} onClose={() => setSelectedRxId(null)} />
    </Stack>
  )
}

export function Modal({ patientId, encounterId, open, onClose }) {
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} scroll="paper">
      <ModalContent patientId={patientId} encounterId={encounterId} onClose={onClose} />
    </Dialog>
  )
}

function ModalContent({ patientId, encounterId, onClose }) {
  const [selectedRxId, setSelectedRxId] = useState(null)

  const { data, isRefreshing, isPending, order, setOrder } = usePrescriptionsData({ patientId, encounterId })

  return (
    <>
      <DialogTitle onClose={onClose}>ERx</DialogTitle>
      <LinearProgress loading={isRefreshing} />
      <DialogContent sx={{ p: 0 }}>
        <Content data={data} isLoading={isPending} order={order} onOrderChange={setOrder} onSelect={setSelectedRxId} />
      </DialogContent>
      <DialogActions sx={{ width: '100%', justifyContent: 'flex-start' }}>
        <Pagination data={data} isLoading={isPending} />
      </DialogActions>
      <PrescriptionDetailsModal prescriptionId={selectedRxId} open={Boolean(selectedRxId)} onClose={() => setSelectedRxId(null)} />
    </>
  )
}

function usePrescriptionsData({ patientId, encounterId, perPage: perPageOverride }) {
  const [order, setOrder] = useState(RxSortOrder.DESC)
  const [page, queryPerPage] = usePageParams({ id: 'rxhx' })
  const perPage = perPageOverride ?? queryPerPage

  const query = usePatientPrescriptions(
    patientId,
    {
      encounter_id: encounterId,
      order,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
    {
      enabled: Boolean(patientId),
      placeholderData: keepPreviousData,
    }
  )

  return { ...query, order, setOrder }
}

function Content({ data, isLoading, order, onOrderChange, onSelect, perPage }) {
  const showLoading = isLoading
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <TableContainer>
      <Table size="small" sx={{ '& tbody tr:last-of-type td': { borderBottom: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Drug</TableCell>
            <TableCell>Diagnosis</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell sortDirection={order}>
              <TableSortLabel
                active
                direction={order}
                onClick={() => onOrderChange(order === RxSortOrder.DESC ? RxSortOrder.ASC : RxSortOrder.DESC)}
              >
                ERx Date
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showLoading && <TableLoader columns={5} items={perPage} />}
          {showEmpty && <TableEmpty columns={5} />}
          {showData && data?.map((rx) => <Prescription key={rx.id} data={rx} onSelect={() => onSelect(rx.id)} />)}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function Prescription({ data: rx, onSelect }) {
  return (
    <TableRow hover onClick={onSelect} sx={[{ cursor: 'pointer' }, isCancelled(rx.rxStatus) && { opacity: 0.4 }]}>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Typography>{`${rx.drugName}${rx.isRefill ? ' (Refill)' : ''}`}</Typography>
        {rx.reasonForMedication && <Typography sx={{ color: 'text.secondary' }}>{rx.reasonForMedication}</Typography>}
        <Typography sx={{ color: 'text.secondary' }}>{rx?.pharmacy?.name}</Typography>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Tooltip title={rx.diagnosisDescription ? rx.diagnosisDescription : ''}>
          <Typography>{rx.diagnosis}</Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Stack direction="row" spacing={1}>
          <StatusChip label={`${rx.quantity}+${rx.refills}`} type="warning" />
          <StatusChip label={rx.remain || 0} type="success" />
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Typography>{rx.provider?.name}</Typography>
        <Typography>{[rx.provider?.city, rx.provider?.state].filter(Boolean).join(', ')}</Typography>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Typography>{rx.createdAt ? dayjs(rx.createdAt).format('L') : ''}</Typography>
        <StatusChip.Rx status={rx.rxStatus} />
      </TableCell>
    </TableRow>
  )
}

function Pagination({ data, isLoading, perPage: perPageOverride, disableRowsPerPage }) {
  const [page, queryPerPage, setPageParams] = usePageParams({ id: 'rxhx' })
  const perPage = perPageOverride ?? queryPerPage

  return (
    <PaginationComp
      id="rxhx"
      loading={isLoading}
      last={data?.length < perPage}
      page={page}
      perPage={perPage}
      setPageParams={(params) => setPageParams(perPageOverride ? { page: params.page } : params)}
      disableRowsPerPage={disableRowsPerPage}
    />
  )
}
